import clsx from "clsx";
import type { ReactNode } from "react";
import type { Tone } from "../status/statusMap";
import { RiskMeter, riskLevelOf } from "../status/RiskMeter";
import { TonePill } from "../status/StatusBadge";

/** Tones accepted by the generic Badge: the five status tones plus the legacy names `good` and `brand`. */
export type BadgeTone = Tone | "good" | "brand";

function normalise(tone: BadgeTone): Tone | "brand" {
  return tone === "good" ? "ok" : tone;
}

/**
 * Generic pill for counts and facts. For statuses use `StatusBadge` (marker + i18n label + help).
 * Brand-toned badges are reserved for "yours / selected" facts, never for status.
 */
export function Badge({ tone = "neutral", children, className, compact }: { tone?: BadgeTone; children: ReactNode; className?: string; compact?: boolean }) {
  const t = normalise(tone);
  if (t === "brand") {
    return <span className={clsx("inline-flex items-center whitespace-nowrap rounded-full bg-brand-soft px-2 font-semibold text-brand-text", compact ? "h-5 text-xs" : "h-6 text-[12.5px]", className)}>{children}</span>;
  }
  return <TonePill tone={t} compact={compact} marker={false} className={className}>{children}</TonePill>;
}

/**
 * @deprecated Maps bare status words to a legacy tone. Use `toneOf(domain, status)` from the status map instead.
 * Kept so existing dashboards keep compiling; note that rejected/revoked/expired are neutral now (§2.1).
 */
export function statusTone(s?: string | null): BadgeTone {
  switch ((s ?? "").toLowerCase()) {
    case "active": case "enabled": case "succeeded": case "approved": case "completed": case "published": case "in_sync": case "online": case "sent": case "present": case "verified": case "healthy": return "good";
    case "pending": case "pending_approval": case "queued": case "running": case "provisioning": case "submitted": case "requested": case "pending_add": case "pending_remove": case "email_verification": return "info";
    case "awaiting_review": case "needs_review": case "orphan": case "open": case "escalated": case "expiring": case "held": case "overdue": case "degraded": case "critical": case "high": return "warn";
    case "failed": case "error": case "offline": case "suspended": case "quarantined": case "blocked": return "bad";
    default: return "neutral";
  }
}

/** @deprecated Use `RiskMeter` with `riskLevelOf(risk)`. Kept for the product apps; renders the meter. */
export function RiskBadge({ risk }: { risk?: string | null }) {
  return <RiskMeter level={riskLevelOf(risk)} />;
}
